import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import {
  Form,
  FormError,
  PageHeader,
  Panel,
  SubmitButton,
  TextField,
  fieldErrors,
  useNotifications,
} from '../../components';
import { PATHS, staffGroupPath } from '../../routes/paths';
import { useCreateGroup } from '../queries';
import { GROUPS_COPY, isSlugTaken, slugTakenField } from './groups';

/**
 * Registering an economic group: a name and a slug, and nothing else.
 *
 * Split out of the list for the reason the companies and insurers are. The
 * success path differs from theirs in one way: it lands on the new group's own
 * page rather than on the list, because a group is born empty and the next
 * thing anybody does with one is put companies in it -- which is done there.
 *
 * A taken slug comes back as a bare `409` naming no field, so the screen
 * supplies the field itself, the same reading `NewStaffUserPage` makes of a
 * taken e-mail. The slug named is the one that was *sent*, not whatever the
 * field holds by the time the answer arrives.
 */
export function NewGroupPage() {
  const create = useCreateGroup();
  const navigate = useNavigate();
  const { notify } = useNotifications();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');

  const errors = fieldErrors(create.error);
  const slugTaken = isSlugTaken(create.error);
  const slugError =
    errors['slug'] ??
    (slugTaken && create.variables !== undefined
      ? slugTakenField(create.variables.slug)
      : undefined);

  const submit = () => {
    create.mutate(
      { name: name.trim(), slug: slug.trim() },
      {
        onSuccess: (created) => {
          notify('success', GROUPS_COPY.registered);
          void navigate(staffGroupPath(created.id));
        },
      },
    );
  };

  return (
    <div className="page">
      <PageHeader
        title={GROUPS_COPY.register}
        description={GROUPS_COPY.registerDescription}
        actions={
          <Link className="button button--ghost" to={PATHS.staffGroups}>
            {GROUPS_COPY.back}
          </Link>
        }
      />

      <Panel title={GROUPS_COPY.register} description={GROUPS_COPY.registerDescription}>
        <Form onSubmit={submit} aria-label={GROUPS_COPY.register}>
          <FormError
            error={create.error}
            {...(slugTaken && { message: GROUPS_COPY.slugTakenForm })}
          />
          <TextField
            label={GROUPS_COPY.nameLabel}
            value={name}
            onChange={setName}
            hint={GROUPS_COPY.nameHint}
            required
            {...(errors['name'] !== undefined && { error: errors['name'] })}
          />
          <TextField
            label={GROUPS_COPY.slugLabel}
            value={slug}
            onChange={setSlug}
            hint={GROUPS_COPY.slugHint}
            required
            {...(slugError !== undefined && { error: slugError })}
          />
          <div className="form__actions">
            <SubmitButton pending={create.isPending} pendingLabel={GROUPS_COPY.registering}>
              {GROUPS_COPY.register}
            </SubmitButton>
            <Link className="button button--ghost" to={PATHS.staffGroups}>
              {GROUPS_COPY.cancel}
            </Link>
          </div>
        </Form>
      </Panel>
    </div>
  );
}
